import type { ReactNode } from 'react';

/**
 * The extras of one service row of "Where it plays" (design spotify-canvas d3, d4, option B): a
 * box under the link of the row for the things that Music24 sends to that service for the song
 * (the Spotify Canvas now; later the music video to YouTube and other auto uploads). The box knows
 * no service: each service gives a list of items.
 */

/** The chip tones of the panel (design spotify-canvas README "Chips"). */
export type ServiceExtraChipTone = 'ok' | 'work' | 'bad' | 'review' | 'outline' | 'muted';

export interface ServiceExtraChip {
  id: string;
  text: string;
  tone: ServiceExtraChipTone;
}

/** `primary`: btn-primary. `button`: the plain button. `link`: a text link. */
export type ServiceExtraActionLook = 'primary' | 'button' | 'link';

export interface ServiceExtraAction {
  id: string;
  label: string;
  look: ServiceExtraActionLook;
  /** An outside address: the action is a link that opens in a new tab. */
  href?: string;
  disabled?: boolean;
  onClick?: () => void;
}

/** The auto upload switch of an item (design d3 "Auto upload", in place, with no extra click). */
export interface ServiceExtraAutoUpload {
  title: string;
  /** The line under the title: on for which songs, off, or read only. */
  sub: string;
  /** The accessible name of the switch. */
  switchLabel: string;
  isOn: boolean;
  /** The save runs: the spinner knob. */
  isSaving: boolean;
  /** Off and dimmed (the demo artist, no settings yet). */
  isDimmed: boolean;
  onToggle: () => void;
  /** A line under the switch, for example "Terms accepted on <date>". */
  note?: string | null;
  /** The line of a refused save. */
  error?: string | null;
}

export interface ServiceExtraItem {
  /** Unique in the box; also the test id `service-extra-<id>`. */
  id: string;
  title: string;
  /** The small picture in the first column (for example the 9:16 loop). None: no column. */
  thumb?: ReactNode;
  chips: ServiceExtraChip[];
  /** The state sentence. */
  status: string;
  /** The sentence in the neg colour (failed, signed out). */
  statusIsBad?: boolean;
  /** More lines under the sentence (a watermark note, a progress bar). */
  details?: ReactNode;
  actions: ServiceExtraAction[];
  /** The line of a refused action, under the buttons. */
  actionError?: string | null;
  autoUpload?: ServiceExtraAutoUpload | null;
  /** An action of the artist waits: the box opens by itself (design d3 fold rule). */
  waits: boolean;
}

/**
 * The fold rule of design d3: the box is open when an item waits for the artist, and folds to one
 * line for each item when nothing waits. The Hide / Show of the artist wins for the page view.
 */
export function serviceExtrasOpen(items: readonly ServiceExtraItem[], choice: boolean | null): boolean {
  return choice ?? items.some((item) => item.waits);
}
